import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Dataset,
  OrganizationsDatasetUpdateBody,
  useGetOrganizationDatasetsQuery,
  useUpdateOrganizationDatasetsMutation,
} from '@verantos-dev/data-access-organizations';
import { DialogMessageBox, TransferListItem } from '@verantos-dev/shared-components';

import AssignmentConfirmDialog from '../components/AssignmentConfirmDialog';
import {
  DialogActionType,
  useOrganizationsListDialogContext,
} from '../context/OrganizationsListDialogContext';

export const useAssignmentDialog = (organizationId: string) => {
  const intl = useIntl();
  const { data, isLoading, isFetching, isError } = useGetOrganizationDatasetsQuery(organizationId);
  const { dispatch } = useOrganizationsListDialogContext();
  const [
    updateAssignedDatasets,
    { isLoading: isUpdateAssignmentLoading, error: updateAssignmentError },
  ] = useUpdateOrganizationDatasetsMutation();

  const [leftItem, setLeftItem] = useState<TransferListItem[]>([]);
  const [rightItem, setRightItem] = useState<TransferListItem[]>([]);
  const [isValidToSubmit, setIsValidToSubmit] = useState(false);

  useEffect(() => {
    if (data && data.data) {
      setLeftItem(data.data.notAssigned.map((dataset) => mapDatasetToTransferListItem(dataset)));
      setRightItem(data.data.assigned.map((dataset) => mapDatasetToTransferListItem(dataset)));
    }
  }, [data, isLoading, isFetching]);

  function mapDatasetToTransferListItem(item: Dataset): TransferListItem {
    return {
      id: item.releaseId,
      value: item.releaseName,
    } as TransferListItem;
  }

  // fetch strings
  const dialogHeader = intl.formatMessage({
    id: 'organizations.datasetsDialog.title',
    defaultMessage: 'Manage data sets',
  });
  const dialogSubHeader = intl.formatMessage({
    id: 'organizations.datasetsDialog.subTitle',
    defaultMessage: 'Data sets',
  });
  const dialogSaveButton = intl.formatMessage({
    id: 'organizations.datasetsDialog.saveButton',
    defaultMessage: 'Save changes',
  });
  const dialogSubmittingSaveButton = intl.formatMessage({
    id: 'organizations.createEditDialog.savingChangesBtnLabel',
    defaultMessage: 'Save changes',
  });
  const dialogConfirmButton = intl.formatMessage({
    id: 'common.button.confirm',
    defaultMessage: 'Confirm',
  });

  const leftTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.leftTitle',
    defaultMessage: 'Unavailable',
  });
  const rightTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.rightTitle',
    defaultMessage: 'Available',
  });
  const description = intl.formatMessage({
    id: 'organizations.datasetsDialog.description',
    defaultMessage: 'Available',
  });

  const confirmationTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.title',
    defaultMessage: 'Confirm changes',
  });
  const availableDataSetsTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.availableDataSets',
    defaultMessage: 'Available data sets',
  });
  const madeAvailableDataSetsTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.madeAvailableDataSets',
    defaultMessage: 'Made available data sets',
  });
  const madeUnavailableDataSetsTitle = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.madeUnavailableDataSets',
    defaultMessage: 'Made unavailable data sets',
  });
  const noChange = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.noChange',
    defaultMessage: 'No Change',
  });
  const successText = intl.formatMessage({
    id: 'organizations.datasetsDialog.confirmationStep.successText',
    defaultMessage: 'Available data sets have been updated successfully.',
  });

  const onClose = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  const onSubmit = () => {
    try {
      const assigned = rightItem.map((item) => item.id);
      const notAssigned = leftItem.map((item) => item.id);
      const datasetsUpdate = { assigned, notAssigned } as OrganizationsDatasetUpdateBody;
      updateAssignedDatasets({ organizationId, datasetsUpdate });
    } catch (error) {
      console.error('Error updating organization datasets', error);
    }
  };

  const getFormAssignmentChanges = (
    leftSelected: TransferListItem[],
    rightSelected: TransferListItem[]
  ) => {
    const allPreviousAssignedDataSets = data?.data?.assigned.map((item) => item.releaseId);
    const allPreviousNotAssignedDataSets = data?.data?.notAssigned.map((item) => item.releaseId);

    const availableDataSets = rightSelected.map((item) => item.value);
    const madeUnavailableDataSets = leftSelected
      .filter((item) => !allPreviousNotAssignedDataSets?.includes(item.id))
      .map((item) => item.value);
    const madeAvailableDataSets = rightSelected
      .filter((item) => !allPreviousAssignedDataSets?.includes(item.id))
      .map((item) => item.value);
    return { madeAvailableDataSets, availableDataSets, madeUnavailableDataSets };
  };

  const onChange = (left: TransferListItem[], right: TransferListItem[]) => {
    setLeftItem(left);
    setRightItem(right);
    const { madeAvailableDataSets, madeUnavailableDataSets } = getFormAssignmentChanges(
      left,
      right
    );
    setIsValidToSubmit(madeAvailableDataSets.length > 0 || madeUnavailableDataSets.length > 0);
  };

  const confirmationStep = (): JSX.Element => {
    const { madeAvailableDataSets, availableDataSets, madeUnavailableDataSets } =
      getFormAssignmentChanges(leftItem, rightItem);

    if (madeAvailableDataSets.length === 0) {
      madeAvailableDataSets.push(noChange);
    }
    if (madeUnavailableDataSets.length === 0) {
      madeUnavailableDataSets.push(noChange);
    }
    return (
      <DialogMessageBox
        id="submit-confirmation-dataset-update"
        type="info"
        text={
          <AssignmentConfirmDialog
            madeAvailableDataSets={madeAvailableDataSets}
            availableDataSets={availableDataSets}
            madeUnavailableDataSets={madeUnavailableDataSets}
            madeAvailableDataSetsTitle={madeAvailableDataSetsTitle}
            availableDataSetsTitle={availableDataSetsTitle}
            madeUnavailableDataSetsTitle={madeUnavailableDataSetsTitle}
          />
        }
        title={confirmationTitle}
      />
    );
  };

  return {
    dialogHeader,
    leftTitle,
    rightTitle,
    isLoading,
    isFetching,
    isError,
    leftItem,
    rightItem,
    isUpdateAssignmentLoading,
    updateAssignmentError,
    description,
    dialogSubHeader,
    dialogSaveButton,
    dialogConfirmButton,
    dialogSubmittingSaveButton,
    isValidToSubmit,
    successText,
    onClose,
    onChange,
    onSubmit,
    confirmationStep,
  };
};
